/**
 * ParticipantRenderer - Renders participant boxes, labels, and lifelines as SVG
 */

import type { ParticipantLayout } from '../layout/types';
import { isSubsystemReference, getDisplayName } from '../hierarchical-utils';

export interface ParticipantRenderOptions {
  /**
   * Width of each participant box in pixels
   */
  boxWidth: number;
  /**
   * Height of each participant box in pixels
   */
  boxHeight: number;
  /**
   * Y position where participant boxes start
   */
  startY: number;
  /**
   * CSS class for focused participants
   */
  focusClass: string;
  /**
   * CSS class for dimmed participants
   */
  dimClass: string;
}

/**
 * Default rendering options for participants
 */
export const DEFAULT_PARTICIPANT_OPTIONS: ParticipantRenderOptions = {
  boxWidth: 180,
  boxHeight: 60,
  startY: 20,
  focusClass: 'participant-focused',
  dimClass: 'participant-dimmed',
};

/**
 * Wraps text into multiple lines based on character limit
 */
function wrapText(text: string, maxCharsPerLine: number): string[] {
  if (text.length <= maxCharsPerLine) {
    return [text];
  }

  const words = text.split(/(?=[A-Z])|[\s_-]+/); // Split on camelCase, spaces, underscores, hyphens
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine + word;
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [text];
}

/**
 * Renders a participant box with label at a specific Y position
 */
export function renderParticipantBox(
  position: ParticipantLayout,
  y: number,
  boxWidth: number,
  boxHeight: number
): string {
  const x = position.x;
  const parts: string[] = [];
  const isSubsystem = isSubsystemReference(position.name);
  const displayName = getDisplayName(position.name);

  // Draw the box with appropriate class
  const boxClass = isSubsystem
    ? 'participant-box participant-subsystem'
    : 'participant-box';

  // Apply custom color if specified
  const colorStyle = position.color ? ` style="fill: ${position.color};"` : '';

  parts.push(
    `<rect x="${x}" y="${y}" width="${boxWidth}" height="${boxHeight}" ` +
      `class="${boxClass}" rx="4"${colorStyle} />`
  );

  // Draw the label (centered in box)
  const textX = position.centerX;
  const textY = y + boxHeight / 2;

  // For subsystems, add an icon indicator before the text
  if (isSubsystem) {
    // Subsystem icon: a small package/box symbol
    const iconSize = 14;
    const iconPadding = 12; // Distance from left edge of box
    const iconX = x + iconPadding;
    const iconY = textY - iconSize / 2;

    parts.push(
      `<g class="subsystem-icon">` +
        `<rect x="${iconX}" y="${iconY}" width="${iconSize}" height="${iconSize}" ` +
        `fill="none" stroke="currentColor" stroke-width="1.5" rx="2" />` +
        `<line x1="${iconX + 3}" y1="${iconY + iconSize / 2}" ` +
        `x2="${iconX + iconSize - 3}" y2="${iconY + iconSize / 2}" ` +
        `stroke="currentColor" stroke-width="1.5" />` +
        `</g>`
    );
  }

  // Wrap text if it's too long (max 20 chars per line for 180px box)
  const maxCharsPerLine = Math.floor(boxWidth / 9); // Approximate: 9px per character
  const lines = wrapText(displayName, maxCharsPerLine);
  const lineHeight = 18; // pixels between lines
  const totalTextHeight = lines.length * lineHeight;
  const startY = textY - totalTextHeight / 2 + lineHeight / 2;

  if (lines.length === 1) {
    // Single line - use simple text element
    parts.push(
      `<text x="${textX}" y="${textY}" class="participant-label" ` +
        `text-anchor="middle" dominant-baseline="middle">${displayName}</text>`
    );
  } else {
    // Multiple lines - use tspan elements
    parts.push(
      `<text x="${textX}" y="${startY}" class="participant-label" text-anchor="middle">`
    );
    lines.forEach((line, index) => {
      const dy = index === 0 ? 0 : lineHeight;
      parts.push(`  <tspan x="${textX}" dy="${dy}">${line}</tspan>`);
    });
    parts.push('</text>');
  }

  return parts.join('\n');
}

/**
 * Renders a vertical lifeline for a participant
 */
export function renderLifeline(
  position: ParticipantLayout,
  startY: number,
  endY: number
): string {
  const x = position.centerX;

  return (
    `<line x1="${x}" y1="${startY}" x2="${x}" y2="${endY}" ` +
    `class="lifeline" stroke-dasharray="5,5" />`
  );
}

/**
 * Renders all participants with their lifelines and bottom boxes
 */
export function renderParticipants(
  positions: ParticipantLayout[],
  totalHeight: number,
  options: ParticipantRenderOptions = DEFAULT_PARTICIPANT_OPTIONS
): string {
  const parts: string[] = [];
  const { startY, boxHeight, boxWidth } = options;
  const lifelineStartY = startY + boxHeight + 10;
  const lifelineEndY = totalHeight - boxHeight - 30;
  const bottomBoxY = lifelineEndY + 10;

  parts.push('<g class="participants">');

  for (const position of positions) {
    parts.push(`  <g id="participant-${position.name}">`);

    // Draw top participant box
    parts.push(
      '    ' + renderParticipantBox(position, startY, boxWidth, boxHeight)
    );

    // Draw lifeline
    parts.push('    ' + renderLifeline(position, lifelineStartY, lifelineEndY));

    // Draw bottom participant box (Mermaid-style)
    parts.push(
      '    ' + renderParticipantBox(position, bottomBoxY, boxWidth, boxHeight)
    );

    parts.push('  </g>');
  }

  parts.push('</g>');

  return parts.join('\n');
}
